<template>
  <div class="room-create-container">
    <h2>{{ t('room_create.title') }}</h2>
    <form @submit.prevent="handleCreateRoom" class="room-create-form">
      <div class="form-group">
        <label for="hostName">{{ t('room_create.host_name') }}</label>
        <input type="text" id="hostName" v-model="hostName" :placeholder="t('room_create.host_name_placeholder')"
          required />
      </div>
      <div class="form-group checkbox-group">
        <label>
          <input type="checkbox" v-model="isPrivate" />
          {{ t('room_create.private_room') }}
        </label>
      </div>
      <div class="form-group">
        <label for="roomName">{{ t('room_create.room_name_label') }}</label>
        <input type="text" id="roomName" v-model="roomName" required />
      </div>
      <div class="form-group">
        <label for="roomType">{{ t('room_create.game_mode_label') }}</label>
        <select id="roomType" v-model="roomType">
          <option value="mix">{{ t('room_create.game_mode_mix') }}</option>
          <option value="zoom_out">{{ t('room_create.game_mode_zoom_out') }}</option>
          <option value="resolution">{{ t('room_create.game_mode_resolution') }}</option>
          <option value="noise">{{ t('room_create.game_mode_noise') }}</option>
        </select>
      </div>
      <div class="form-group">
        <label for="maxPlayers">{{ t('room_create.max_players') }}</label>
        <input type="number" id="maxPlayers" v-model.number="maxPlayers" min="2" max="10" required />
      </div>
      <button type="submit" :disabled="isLoading">{{ t('room_create.create_room_button') }}</button>
    </form>
    <p v-if="error" class="error-message">{{ error }}</p>
    <p v-if="isLoading">{{ t('room_create.creating') }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useRoomStore } from '@/stores/room';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const roomStore = useRoomStore();
const { t } = useI18n();

const hostName = ref('');
const roomName = ref('');
const roomType = ref('mix');
const maxPlayers = ref(4);
const isPrivate = ref(false);
const isLoading = ref(false);
const error = ref<string | null>(null);

const handleCreateRoom = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    const newRoom = await roomStore.createRoom(roomName.value, maxPlayers.value, hostName.value, isPrivate.value, roomType.value);
    if (newRoom && newRoom.id) {
      router.push({ name: 'RoomDetail', params: { roomId: newRoom.id } });
    } else {
      throw new Error('部屋作成後、部屋情報が取得できませんでした。');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error('部屋作成エラー:', err);
    error.value = err.response?.data?.detail || '部屋の作成に失敗しました。';
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.room-create-container {
  max-width: 400px;
  margin: 50px auto;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  text-align: center;
}

h2 {
  color: #333;
  margin-bottom: 20px;
}

.room-create-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-group {
  text-align: left;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #555;
}

input[type="text"],
input[type="number"] {
  width: calc(100% - 20px);
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  background-color: white;
}

button {
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s ease;
}

button:hover:not(:disabled) {
  background-color: #0056b3;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.error-message {
  color: #e74c3c;
  margin-top: 10px;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.checkbox-group input {
  width: auto;
}
</style>
